import { ButtonCus } from '@/components/common/Button';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/hooks/useStore';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import yup from '@/services/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import { Label } from '@/components/common/Label';
import { flowResult } from 'mobx';
import { isPathEk } from '@/utils/routes';
import { ekDomain, ROUTES } from '@/configs/constants';
import { ReservationServiceItem, ReservationServices } from '@/types/reservation';
import { FORM_TYPE, FormType, OPTION_FORMAT } from '@/types/enums';
import { Checkbox } from '@/components/common/Checkbox';
import FileUpload from '@/components/general/FileUpload';
import ImageUploadShow from '@/components/general/ImageUploadShow';
import { FileInfor } from '@/types/bike';
import classNames from 'classnames';
import { Logo } from '@/components/general/logo/Logo';

interface ReservationReportBikePageProps {
    formType?: FormType
}

export default observer(function ReservationReportBikePage({ formType = FORM_TYPE.Create }: ReservationReportBikePageProps) {
    // hooks
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);

    const isView = formType === FORM_TYPE.View;
    const isCreate = formType === FORM_TYPE.Create;

    // store
    const {
        reservationStore: {
            getReportBikeDetail, postReportBike, getReservationsReportHistory
        },
        modalStore: {
            showAlertModal
        }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        parkingLocation: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        keyBoxPosition: yup
            .string()
            .trim()
            .required('form_error_validate.required'),
        bikePhotos: yup
            .array(yup.string().required('form_error_validate.required'))
            .min(1, 'form_error_validate.required') // Bắt buộc có ít nhất 1 ảnh
            .required('form_error_validate.required'),
        note: yup
            .string()
            .trim()
            .default(''),
        isCheckOption: yup.boolean().required('form_error_validate.required'),
        isConfirm: yup.boolean().required('form_error_validate.required'),
        isCheckBike: yup.boolean().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            parkingLocation: '',
            keyBoxPosition: '',
            bikePhotos: [],
            note: '',
            isCheckOption: false,
            isConfirm: false,
            isCheckBike: false
        }
    });

    const watchCheckOption = form.watch('isCheckOption');
    const watchConfirm = form.watch('isConfirm');
    const watchCheckBike = form.watch('isCheckBike');

    // state
    const [fileCerUploads, setFileCerUploads] = useState<FileInfor[] | null>([]);
    const [services, setServices] = useState<ReservationServices>({} as ReservationServices);

    const additionalServices: ReservationServiceItem[] = useMemo(() => {
        let returnServices: ReservationServiceItem[] = [];
        const { option = [], compensation = [], dropOff = [], extraMileage = [] } = services || {};

        returnServices = [
            ...option.map(item => ({ ...item, format: OPTION_FORMAT.Option })),
            ...compensation.map(item => ({ ...item, format: OPTION_FORMAT.Insurance })),
            ...extraMileage.map(item => ({ ...item, format: OPTION_FORMAT.ExtraMileage })),
            ...dropOff.map(item => ({ ...item, format: OPTION_FORMAT.DropOff }))
        ];
        return returnServices;
    }, [services]);

    // lifecycle
    useEffect(() => {
        isCreate && id && fetchBeforeReportInfor(id);
        isView && id && fetchReportHistoryInfor(id);
    }, [id]);

    // function
    // const onFetch = async (bikeId: number | string) => {
    //     if (bikeId) {
    //         const res = await flowResult();
    //         form.setValue('rentalLocation', (res && res?.rentalLocation) ? res?.rentalLocation : '');
    //     } else {
    //         form.setValue('rentalLocation', '');
    //     }
    // };

    const fetchBeforeReportInfor = async (id: number | string) => {
        const res = await flowResult(getReportBikeDetail({ id }));
        if (res) {
            setServices(res.services || {});
            if (res?.returnDate || res?.completeVehicleDate) {
                showAlertModal({
                    id: 'completed-set-up-bike',
                    type: 'error',
                    content: t('messages.completed_set_up_bike'),
                    onClickXIcon: () => {
                        navigate(isEk ?
                            `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}` :
                            `${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
                    }
                });
            }
        }
    };

    const fetchReportHistoryInfor = async (id: number | string) => {
        const res = await flowResult(getReservationsReportHistory(id));
        if (res) {
            setServices(res.services || {});
            form.reset({
                parkingLocation: res.parkingLocation,
                keyBoxPosition: res.keyBoxPosition,
                bikePhotos: res.bikePhotos?.length > 0 ? res.bikePhotos.map(item => item.id.toString()) : [],
                note: res.note,
                isCheckOption: true,
                isConfirm: true,
                isCheckBike: true
            });
            setFileCerUploads(res.bikePhotos?.length > 0 ? [...res.bikePhotos] : []);
        }
    };

    const handleUploadFileCertification = (fileList: any[]) => {
        let currentFileList = fileCerUploads ? [...fileCerUploads] : [];
        let numberImgFiles = currentFileList?.length;
        if (numberImgFiles >= 3) {
            showAlertModal({
                id: 'error',
                type: 'error',
                content: t('messages.maximum_image_upload')
            });
        } else {
            fileList.forEach((file) => {
                currentFileList.push({
                    id: file.id,
                    fileName: file.fileName,
                    fileType: file.fileType,
                    filePath: file.path,
                    mimeType: file.mimeType
                });
            });
            setFileCerUploads([...currentFileList]);
            form.setValue('bikePhotos', currentFileList.map(file => file.id), { shouldValidate: true });
        }
    };

    const handleRemoveImage = (path: string) => {
        if (fileCerUploads) {
            let filterFileList = fileCerUploads?.filter(file => file.filePath !== path);
            setFileCerUploads([...filterFileList]);
            form.setValue('bikePhotos', filterFileList.map(file => file.id));
        }
    };

    const onSubmit = async (data: FormData) => {
        // const { rentalLocation, ...dataSubmit } = data;
        const res = await flowResult(postReportBike(Number(id), data));
        if (res) {
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.completed_set_up_bike'),
                onClickXIcon: () => {
                    navigate(isEk ?
                        `/${ekDomain}${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}` :
                        `${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`);
                }
            });
        }
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='grid gap-4 md:gap-6'>
                        <div className='w-full flex justify-center'>
                            <div className='logo w-[min(240px,55vw)]'>
                                <Logo />
                            </div>
                        </div>
                        <div className='text-[min(24px,calc(100vw/17))] xsm:text-fs-mid24 leading-lh-mid34 font-semibold text-center whitespace-normal xsm:pb-[min(40px,6vw)] pb-4'>
                            {isEk ? t('words_title.report_bike_ek') : t('words_title.report_bike_gas')}
                        </div>

                        {(additionalServices && additionalServices.length > 0) && (
                            <div className='flex sm:flex-row flex-col sm:gap-6 xsm:gap-2 gap-1 flex-wrap'>
                                <Label className='w-[150px]'>{t('field.select_option')}</Label>
                                <div className='xsm:flex hidden gap-20 xsm:justify-start justify-between '>
                                    <div className='flex flex-col sm:gap-4 gap-2 w-fit'>
                                        {
                                            additionalServices.map((service, index) => {
                                                return (
                                                    <label key={index} className='md:whitespace-nowrap'>{service.name}</label>
                                                );
                                            })
                                        }
                                    </div>
                                    <div className='flex flex-col sm:gap-4 gap-2'>
                                        {
                                            additionalServices.map((service, index) => {
                                                return (
                                                    <div key={index} className='flex justify-between md:gap-20'>
                                                        <p>{service?.quantity}</p>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                </div>
                                <div className='flex flex-col xsm:hidden'>
                                    {
                                        additionalServices.map((service, index) => {
                                            return (
                                                <div key={index} className='flex justify-between gap-4'>
                                                    <label className='md:whitespace-nowrap'>{service.name}</label>
                                                    <p>{service?.quantity}</p>
                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            </div>
                        )}

                        {/* Infor label */}
                        <FormField
                            control={form.control}
                            name='parkingLocation'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.reservation.parking_location')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                            disabled={isView}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='keyBoxPosition'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.reservation.keybox_location')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('placeholder.enter')}
                                            {...field}
                                            disabled={isView}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='col-span-full'>
                            <FormLabel required>{t('field.report_bike_image')}</FormLabel>
                            <p className='text-black'>{t('sentences.description_report_img')}</p>
                            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-3 md:gap-6'>
                                {isCreate &&
                                    (
                                        <FileUpload
                                            fieldName='bikePhotos'
                                            form={form}
                                            required={true}
                                            className={classNames('w-full')}
                                            placeholder={t('field.choose_image')}
                                            acceptUpload='image/*'
                                            handleUploadFile={handleUploadFileCertification}
                                        />
                                    )}
                                {
                                    fileCerUploads && fileCerUploads?.length > 0 && (
                                        <>
                                            {fileCerUploads.map((file, index) => {
                                                return (
                                                    <div key={index}>
                                                        <ImageUploadShow isShowX={isCreate} clearImg={handleRemoveImage} urlImagePreview={file.filePath || ''} />
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )
                                }
                            </div>
                        </div>
                        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6'>
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>{t('words_title.note')}</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-28'
                                                {...field}
                                                disabled={isView}
                                            />
                                        </FormControl>
                                        <FormMessage className='mt-1' />
                                    </FormItem>
                                )}
                            />

                            <div className='flex flex-col gap-4 col-span-full'>
                                <FormField
                                    control={form.control}
                                    name='isCheckOption'
                                    render={({ field }) => (
                                        <FormItem className='flex items-start'>
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={isView}
                                                    className='mt-0.5'
                                                />
                                            </FormControl>
                                            <FormLabel className='font-normal ml-1.5 mb-0 cursor-pointer'>
                                                {t('sentences.set_up_check')}
                                            </FormLabel>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='isConfirm'
                                    render={({ field }) => (
                                        <FormItem className='flex items-start'>
                                            <FormControl>
                                                <Checkbox
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={isView}
                                                    className='mt-0.5'
                                                />
                                            </FormControl>
                                            <FormLabel className='font-normal ml-1.5 mb-0 cursor-pointer'>
                                                {!isEk ? t('sentences.set_up_confirm') : t('sentences.set_up_ek_confirm')}
                                            </FormLabel>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {
                                    !isEk && (
                                        <FormField
                                            control={form.control}
                                            name='isCheckBike'
                                            render={({ field }) => (
                                                <FormItem className='flex items-start'>
                                                    <FormControl>
                                                        <Checkbox
                                                            checked={field.value}
                                                            onCheckedChange={field.onChange}
                                                            disabled={isView}
                                                            className='mt-0.5'
                                                        />
                                                    </FormControl>
                                                    <FormLabel className='font-normal ml-1.5 mb-0 cursor-pointer'>
                                                        {t('sentences.set_up_check_2')}
                                                    </FormLabel>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )
                                }

                            </div>

                        </div>

                    </div>
                    {
                        isCreate && (
                            <div className='flex justify-center mt-6'>
                                <ButtonCus variant='default' size='md' disabled={form.formState.isSubmitting || !(watchCheckOption && watchConfirm && (!isEk ? watchCheckBike : true))}>{t('button.send')}</ButtonCus>
                            </div>
                        )
                    }
                </form>
            </Form>
        </div>
    );
});
